import { getApiBaseUrl } from "../api/baseUrl"
import { throwIfRateLimited } from "../api/rateLimit"
import type {
  ClerkInvitationCreateResponse,
  ClerkInvitationListResponse,
  ClerkInvitationRecord,
  ClerkInvitationRevokeResponse,
} from "../schemas"

async function getJson<TResponse>(
  path: string,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<TResponse> {
  const baseUrl = getApiBaseUrl()
  if (!baseUrl) {
    throw new Error(
      "Invite sync is unavailable because no worker is configured.",
    )
  }

  const response = await fetch(new URL(path, baseUrl), {
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
    signal,
  })

  throwIfRateLimited(response)

  if (!response.ok) {
    const message = await response.text()
    throw new Error(message || "Failed to load invites.")
  }

  return (await response.json()) as TResponse
}

async function postJson<TResponse>(
  path: string,
  sessionToken: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<TResponse> {
  const baseUrl = getApiBaseUrl()
  if (!baseUrl) {
    throw new Error(
      "Invite sync is unavailable because no worker is configured.",
    )
  }

  const response = await fetch(new URL(path, baseUrl), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${sessionToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  })

  throwIfRateLimited(response)

  if (!response.ok) {
    const message = await response.text()
    throw new Error(message || "Invite request failed.")
  }

  return (await response.json()) as TResponse
}

export async function fetchClerkInvitationsFromServer(
  sessionToken: string,
  signal?: AbortSignal,
): Promise<ClerkInvitationRecord[]> {
  const response = await getJson<ClerkInvitationListResponse>(
    "/api/admin/invitations",
    sessionToken,
    signal,
  )
  return response.records
}

export async function createClerkInvitationOnServer(
  emailAddress: string,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<ClerkInvitationRecord> {
  const response = await postJson<ClerkInvitationCreateResponse>(
    "/api/admin/invitations",
    sessionToken,
    { emailAddress },
    signal,
  )
  return response.record
}

export async function revokeClerkInvitationOnServer(
  invitationId: string,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<ClerkInvitationRecord> {
  const response = await postJson<ClerkInvitationRevokeResponse>(
    `/api/admin/invitations/${encodeURIComponent(invitationId)}/revoke`,
    sessionToken,
    {},
    signal,
  )
  return response.record
}
